import ElstrId from "./ElstrId";
import ElstrBaseApp from "./ElstrBaseApp";
import { ELSTR_BACKEND_VERSION } from "./constants/BackendVersion";
import { activeLang } from "./ElstrLanguage";

/**
 * This is the class for IO used in Elstr projects
 *
 * options attributes:
 *     abortStaleRequests: aborts stale requests (only works with requestJsonRpc)
 *
 * @class ElstrIo
 */
export default class ElstrIo {
  static SERVICE_URL = "/services/";
  static COOKIE = "";

  /**
   * Use requestJsonRpc for any JSON-RPC requests to the Elstr server
   * @param className
   * @param methodName
   * @param params
   * @param requestMethod [Optional] POST | GET | PUT, defines the request method to use
   * @param headers [Optional] key value pairs
   * @returns {Request}
   */
  static async requestJsonRpc<P, R>(
    className: string,
    methodName: string,
    params: P,
    requestMethod = "POST",
    headers?: Record<string, string>,
  ): Promise<R> {
    const url = ElstrIo.getReqUrl(className);
    const requestData = ElstrIo.getReqData(methodName, params, headers);

    try {
      const requestCall: any = await fetch(url, requestData);

      const serviceResult = await requestCall.json();
      if (serviceResult.result) {
        if (serviceResult.result.hasOwnProperty("message")) {
          // For backwards compatibility of the Elstr Backend
          // results that contains a "message" key in the result object
          // will be returned as an error
          throw new Error(serviceResult.result.message);
        } else {
          if (serviceResult.result.hasOwnProperty("messages")) {
            const resultMessages = serviceResult.result.messages;

            if (Array.isArray(resultMessages)) {
              resultMessages.forEach(message => {
                if (message.hasOwnProperty("level")) {
                  if (message.level === "EXCEPTION") {
                    if (message.hasOwnProperty("message")) {
                      throw new Error(message.message);
                    } else {
                      throw new Error(message);
                    }
                  }
                }
              });
            }
          }

          return serviceResult.result;
        }
      }

      if (serviceResult.error) {
        throw new Error(serviceResult.error.message);
      }

      throw new Error(`Response to ElstrIo was neither a result nor an error
            Status of response: ${requestCall.status}
            and serviceresult: ${serviceResult}`);
    } catch (e) {
      // We don't want to console.error in a Test runner like Jest
      if (process.env.NODE_ENV !== "test") console.error(e);
      throw e;
    }
  }

  public static getReqData(methodName, params, headers?: Record<string, string>) {
    const body = {
      jsonrpc: "2.0",
      method: methodName,
      params,
      id: ElstrId.create(),
    };

    const requestData: any = {
      credentials: "include",
      method: "POST",
      headers: {
        "accept-encoding": "gzip, deflate",
      },
      body: JSON.stringify(body),
    };

    if (process.env.NODE_ENV === "test") {
      // Apply session cookie in case the service requests requires authentication while using test runners
      requestData.headers.cookie = ElstrIo.COOKIE;
    }

    if (ElstrBaseApp.ELSTR_BACKEND_VERSION === ELSTR_BACKEND_VERSION.BE2) {
      requestData.headers["Accept-Language"] = activeLang() || "en";
    }

    for (const header in headers) {
      requestData.headers[header] = headers[header];
    }

    return requestData;
  }

  private static getReqUrl(className: string): string {
    if (ElstrBaseApp.ELSTR_BACKEND_VERSION === ELSTR_BACKEND_VERSION.BE1) {
      return `${ElstrIo.SERVICE_URL}${className}`;
    }

    if (ElstrBaseApp.ELSTR_BACKEND_VERSION === ELSTR_BACKEND_VERSION.BE2) {
      return `${ElstrIo.SERVICE_URL}json-rpc/${className}`;
    }

    // Fallback BE1 version
    return `${ElstrIo.SERVICE_URL}${className}`;
  }

  static throwErrorIfResponseContainsErrorMessage(response) {
    if (response.hasOwnProperty("messages")) {
      const resultMessages = response.messages;

      if (Array.isArray(resultMessages)) {
        const errorMsgs = resultMessages.filter(message => {
          if (message.hasOwnProperty("level")) {
            if (message.level === "ERROR") {
              return true;
            }
          }
          return false;
        });

        const errorMsgsStrings: any[] = [];
        if (errorMsgs.length > 0) {
          errorMsgs.forEach(msg => {
            console.log(msg.message);
            errorMsgsStrings.push(msg.message);
          });
          throw new Error(errorMsgsStrings.join("; "));
        }
      }
    }
  }
}
